// src/pages/sow-draft/ResourcesTable.jsx
//
// Resource plan -- a wide editable grid (role/level/location/dates/rate),
// optionally pre-filled from JOSS. Plain styled <table>, same reasoning
// as MilestonesTable.

import PropTypes from "prop-types";
import { Plus, X } from "lucide-react";
import { Alert, Button, DatePicker } from "../../shared/ui";
import { formatCurrency } from "./sowDraftHelpers";

const cellInput = "w-full text-sm px-2 py-1.5 rounded border border-line focus:outline-none focus:border-brand-600";

export default function ResourcesTable({
  resources,
  totals,
  hasSfOpportunityId,
  jossLoading,
  onAdd,
  onFetchJoss,
  onUpdate,
  onRemove,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-ink">Resource Plan</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={onAdd}>
            <Plus size={14} /> Add Manual
          </Button>
          <Button
            size="sm"
            onClick={onFetchJoss}
            disabled={jossLoading || !hasSfOpportunityId}
            loading={jossLoading}
            title={!hasSfOpportunityId ? "No Salesforce Opportunity ID available" : "Fetch resource estimates from JOSS"}
          >
            Fetch from JOSS
          </Button>
        </div>
      </div>

      {!hasSfOpportunityId && (
        <Alert variant="info" className="mb-2">
          No Salesforce Opportunity ID found. JOSS fetch is unavailable — use manual entry below.
        </Alert>
      )}

      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full text-sm" style={{ minWidth: 1400 }}>
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-muted">
              <th className="p-2" style={{ width: 130 }}>Role</th>
              <th className="p-2" style={{ width: 90 }}>Level</th>
              <th className="p-2" style={{ width: 110 }}>Location</th>
              <th className="p-2" style={{ width: 110 }}>Technology</th>
              <th className="p-2" style={{ width: 90 }}>Domain</th>
              <th className="p-2" style={{ width: 50 }}>#</th>
              <th className="p-2" style={{ width: 130 }}>Start</th>
              <th className="p-2" style={{ width: 130 }}>End</th>
              <th className="p-2" style={{ width: 80 }}>Hours</th>
              <th className="p-2" style={{ width: 90 }}>Rate</th>
              <th className="p-2 text-right" style={{ width: 110 }}>Amount</th>
              <th className="p-2" style={{ width: 90 }}>Status</th>
              <th className="p-2" style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {resources.map((r, idx) => {
              const amt = r.sellingAmount || (r.count || 1) * (r.hours || 0) * (r.rate || 0);
              return (
                <tr key={idx} className="border-t border-line">
                  <td className="p-1.5">
                    <input className={cellInput} value={r.role} onChange={(e) => onUpdate(idx, "role", e.target.value)} placeholder="Role" title={r.role} />
                  </td>
                  <td className="p-1.5">
                    <input className={cellInput} value={r.level || ""} onChange={(e) => onUpdate(idx, "level", e.target.value)} title={r.level} />
                  </td>
                  <td className="p-1.5">
                    <input className={cellInput} value={r.location || ""} onChange={(e) => onUpdate(idx, "location", e.target.value)} title={r.location} />
                  </td>
                  <td className="p-1.5">
                    <input className={cellInput} value={r.technology || ""} onChange={(e) => onUpdate(idx, "technology", e.target.value)} title={r.technology} />
                  </td>
                  <td className="p-1.5">
                    <input className={cellInput} value={r.domain || ""} onChange={(e) => onUpdate(idx, "domain", e.target.value)} title={r.domain} />
                  </td>
                  <td className="p-1.5">
                    <input type="number" min={1} className={cellInput} value={r.count} onChange={(e) => onUpdate(idx, "count", parseInt(e.target.value) || 1)} />
                  </td>
                  <td className="p-1.5">
                    <DatePicker bare className={cellInput} value={r.startDate || ""} max={r.endDate || undefined} onChange={(iso) => onUpdate(idx, "startDate", iso)} />
                  </td>
                  <td className="p-1.5">
                    <DatePicker bare className={cellInput} value={r.endDate || ""} min={r.startDate || undefined} onChange={(iso) => onUpdate(idx, "endDate", iso)} />
                  </td>
                  <td className="p-1.5">
                    <input
                      type="number"
                      className={cellInput}
                      value={r.hours}
                      onChange={(e) => onUpdate(idx, "hours", parseInt(e.target.value) || 0)}
                      title={r.startDate && r.endDate ? "Auto-calculated from dates" : "Enter manually or set dates"}
                    />
                  </td>
                  <td className="p-1.5">
                    <input type="number" className={cellInput} value={r.rate} onChange={(e) => onUpdate(idx, "rate", parseFloat(e.target.value) || 0)} />
                  </td>
                  <td className="p-2 text-right text-ink">{formatCurrency(amt)}</td>
                  <td className="p-2">
                    {r.status ? (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{r.status}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-1.5 text-center">
                    <button type="button" onClick={() => onRemove(idx)} className="text-red-500 hover:text-red-700">
                      <X size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-line bg-gray-50">
              <td colSpan={8} className="p-2 text-right font-semibold text-ink">
                Total:
              </td>
              <td className="p-2 font-semibold text-ink">{totals.totalHours.toLocaleString()}</td>
              <td />
              <td className="p-2 text-right font-semibold text-ink">{formatCurrency(totals.totalAmount)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

ResourcesTable.propTypes = {
  resources: PropTypes.array.isRequired,
  totals: PropTypes.shape({ totalHours: PropTypes.number, totalAmount: PropTypes.number }).isRequired,
  hasSfOpportunityId: PropTypes.bool.isRequired,
  jossLoading: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onFetchJoss: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};
